/**
 * geographies.ts
 * ----------------
 * Utility functions related to geography values.
 */

import { Americas, AsiaPacific, Europe, MiddleEastAndAfrica } from '../constants/geographies';

/**
 * Gets standardized string representation of a geography value. Primarily used to coerce case-insensitive equivalents
 * into the proper casing.
 * @param value Value to check
 * @returns Normalized geography value if it is a valid value, undefined otherwise
 */
export function getNormalizedGeographyValue (value: string) {
    if (value.toLowerCase() === Americas.toLowerCase()) {
        return Americas;
    }

    if (value.toLowerCase() === AsiaPacific.toLowerCase()) {
        return AsiaPacific;
    }

    if (value.toLowerCase() === Europe.toLowerCase()) {
        return Europe;
    }

    if (value.toLowerCase() === MiddleEastAndAfrica.toLowerCase()) {
        return MiddleEastAndAfrica;
    }

    return undefined;
}

/**
 * @param geography Geography value as defined in the storage
 */
export const getTitleForGeography = (geography: string) => {
    // TODO: if we need to localize text, then figure out a means to pull values from a downloaded resource file?
    switch (geography) {
        case Americas: return 'Americas';
        case AsiaPacific: return 'Asia Pacific';
        case Europe: return 'Europe';
        // TODO: what is the way we are going to show this?  Probably should be consistent.
        case MiddleEastAndAfrica: return 'Middle East and Africa';
        default: return undefined;
    }
};

/**
 * Checks whether the passed array of strings contains all known geographies and nothing more.
 * @param geographies Array of geographies to check.
 * @returns True if array contains all known geographies and nothing more, false otherwise.
 */
export function isAllGeographiesCollection (geographies: string[]) {
    const allGeosSet = new Set([Americas, AsiaPacific, Europe, MiddleEastAndAfrica]);
    const geosSet = new Set(geographies);

    if (allGeosSet.size !== geosSet.size) {
        return false;
    }

    let allContained = true;

    geosSet.forEach(geography => {
        if (!allGeosSet.has(geography)) {
            allContained = false;
        }
    });

    return allContained;
}

/**
 * Checks whether or not the given string value is a valid geography value.
 * @param value Value to check
 * @param ignoreCase Whether or not check should ignore case. Default is false.
 * @returns Whether or not passed value is a valid geography
 */
export function isValidGeography (value: string, ignoreCase = false) {
    const compareValue = ignoreCase ? value.toLowerCase() : value;

    return compareValue === (ignoreCase ? Americas.toLowerCase() : Americas)
        || compareValue === (ignoreCase ? AsiaPacific.toLowerCase() : AsiaPacific)
        || compareValue === (ignoreCase ? Europe.toLowerCase() : Europe)
        || compareValue === (ignoreCase ? MiddleEastAndAfrica.toLowerCase() : MiddleEastAndAfrica);
}
